import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import {
  GET_EVENT_LANDING_PAGE,
  GET_INVITATION_EVENT,
} from "@app/graphql/events.queries";
import { useOrganizationId } from "@app/hooks/use-organization-id";
import { decodeEventDescription } from "@app/lib/event-description";

export type NormalizedEvent = {
  id: string;
  name: string;
  description: string;
  destination: string;
  venue: string;
  venueAddress: string;
  startDate: string;
  endDate: string;
  status: string;
};

export type BookingLink = {
  currency: string;
  eventLink: string;
};

export type NormalizedLandingPage = {
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  registrationOpen: boolean;
  bookingLinks: BookingLink[];
};

interface EventQueryData {
  getEvent: {
    eventId: string;
    eventName: string | null;
    description: string | null;
    destination: string | null;
    venue: string | null;
    venueAddress: string | null;
    startDate: string | null;
    endDate: string | null;
    status: string | null;
  } | null;
}

interface EventQueryVariables {
  eventId: string;
  organizationId: string;
}

interface LandingPageQueryData {
  getEventLandingPage: {
    slug: string;
    title: string;
    subtitle: string | null;
    description: string | null;
    registrationOpen: boolean;
    links: {
      oneWorldBookingLinks: {
        links: BookingLink[];
      } | null;
    } | null;
  } | null;
}

interface LandingPageQueryVariables {
  eventId: string;
}

export type EventInvitationData = {
  event: NormalizedEvent | undefined;
  landingPage: NormalizedLandingPage | null;
  isLoadingEvent: boolean;
  hasLoadError: boolean;
  /**
   * True until the first event/landing-page load settles when there is no
   * cached data yet. Lets pages render a loader instead of flashing default
   * placeholder content (e.g. when a copied link is opened in a new tab).
   */
  isLoading: boolean;
};

type UseEventInvitationDataOptions = {
  authMode?: "authenticated" | "public";
};

const PUBLIC_QUERY_CONTEXT = { skipAuth: true } as const;

/**
 * Shared data layer for the invitation experience. Both the public landing page
 * and the protected edit page read the same event + landing-page records, so
 * this hook centralizes the queries and normalization (DRY). The editor updates
 * Apollo's cache directly after mutations, so no refetch handles are exposed.
 */
export function useEventInvitationData(
  eventId: string | undefined,
  { authMode = "authenticated" }: UseEventInvitationDataOptions = {},
): EventInvitationData {
  const organizationId = useOrganizationId();
  const queryContext = authMode === "public" ? PUBLIC_QUERY_CONTEXT : undefined;

  const {
    data: eventData,
    error: eventError,
    loading: isLoadingEvent,
  } = useQuery<EventQueryData, EventQueryVariables>(GET_INVITATION_EVENT, {
    variables: { eventId: eventId ?? "", organizationId: organizationId ?? "" },
    skip: !eventId || !organizationId,
    context: queryContext,
    fetchPolicy: authMode === "public" ? "network-only" : "cache-and-network",
  });

  const { data: landingData, loading: isLoadingLandingPage } = useQuery<
    LandingPageQueryData,
    LandingPageQueryVariables
  >(GET_EVENT_LANDING_PAGE, {
    variables: { eventId: eventId ?? "" },
    skip: !eventId,
    context: queryContext,
    fetchPolicy: "cache-and-network",
  });

  const liveEvent = eventData?.getEvent;
  const event = useMemo<NormalizedEvent | undefined>(
    () =>
      liveEvent
        ? {
            id: liveEvent.eventId,
            name: liveEvent.eventName ?? "",
            description: decodeEventDescription(liveEvent.description ?? ""),
            destination: liveEvent.destination ?? "",
            venue: liveEvent.venue ?? "",
            venueAddress: liveEvent.venueAddress ?? "",
            startDate: liveEvent.startDate ?? "",
            endDate: liveEvent.endDate ?? "",
            status: liveEvent.status ?? "",
          }
        : undefined,
    [liveEvent],
  );

  const liveLandingPage = landingData?.getEventLandingPage;
  const landingPage = useMemo<NormalizedLandingPage | null>(
    () =>
      liveLandingPage
        ? {
            slug: liveLandingPage.slug,
            title: liveLandingPage.title ?? "",
            subtitle: liveLandingPage.subtitle ?? "",
            description: decodeEventDescription(
              liveLandingPage.description ?? "",
            ),
            registrationOpen: liveLandingPage.registrationOpen,
            bookingLinks:
              liveLandingPage.links?.oneWorldBookingLinks?.links ?? [],
          }
        : null,
    [liveLandingPage],
  );

  // Only treat as loading while a fetch is in flight AND the query has not
  // produced any result yet. Gating on the raw `data` being `undefined` (rather
  // than the normalized `event`/`landingPage`) means a settled "no landing
  // page" result still counts as loaded, so a cache-and-network background
  // refetch never re-flashes the spinner once cached data exists. A cold
  // new-tab load (no cache) shows the loader until the first result arrives.
  const isLoading =
    (isLoadingEvent && (authMode === "public" || eventData === undefined)) ||
    (isLoadingLandingPage && landingData === undefined);

  const hasLoadError = Boolean(eventError);

  return {
    event,
    landingPage,
    isLoadingEvent,
    hasLoadError,
    isLoading,
  };
}
